import type { YmEconomicsDriver, YmEconomicsPiece } from '@/lib/api/schemas/ymarket-pricing.schema'
import { formatRubleAmount } from '@/lib/market-money'
import { unavailableReasonText } from '@/lib/warehouse/labels'

export type MarketDriverKind = YmEconomicsDriver['kind']

const DRIVER_NAMES: Record<MarketDriverKind, string> = { promo: 'Акция', boost: 'Буст' }
const ACTIVE_OPACITY = 1
const INACTIVE_OPACITY = 0.55

function percent(value: number): string {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} %`
}

function dayMonth(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Moscow' })
}

/** The Russian tooltip and accessible name; the same text serves both. */
export function marketDriverDescription(driver: YmEconomicsDriver): string {
  const parts = [driver.active ? driver.name : `Скоро: ${driver.name}`]
  if (driver.discountPercent !== null) parts.push(`скидка ${percent(driver.discountPercent)}`)
  if (driver.bid !== null) parts.push(`ставка ${percent(driver.bid)}`)
  if (driver.startsAt !== null && !driver.active) parts.push(`с ${dayMonth(driver.startsAt)}`)
  if (driver.endsAt !== null) parts.push(`до ${dayMonth(driver.endsAt)}`)
  if (driver.promoPrice !== null) parts.push(`промо-цена ${formatRubleAmount(driver.promoPrice)}`)
  if (driver.endingSoon) parts.push('скоро закончится')
  return parts.join(' · ')
}

/** Active driver first, then an upcoming one; none means the driver is absent. */
export function marketDriverOf(economics: YmEconomicsPiece | null | undefined, kind: MarketDriverKind): YmEconomicsDriver | null {
  const drivers = economics?.drivers.filter((driver) => driver.kind === kind) ?? []
  return drivers.find((driver) => driver.active) ?? drivers[0] ?? null
}

function PromoGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 8.6V2.5A.5.5 0 0 1 2.5 2h6.1a.5.5 0 0 1 .35.15l5 5a.5.5 0 0 1 0 .7l-6.1 6.1a.5.5 0 0 1-.7 0l-5-5A.5.5 0 0 1 2 8.6Z" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="5.3" cy="5.3" r="1.1" fill="currentColor" />
    </svg>
  )
}

function BoostGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M9 1.5 3.5 9H8l-1 5.5L12.5 7H8l1-5.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  )
}

export function MarketDriverIcon({ economics, kind }: { economics: YmEconomicsPiece | null | undefined; kind: MarketDriverKind }) {
  const driver = marketDriverOf(economics, kind)
  if (driver === null) {
    const reason = kind === 'boost'
      ? economics?.waterfall.find((step) => step.key === 'boost')?.unavailableReason ?? null
      : null
    const title = economics === null || economics === undefined
      ? unavailableReasonText('ymarket_economics_unavailable')
      : reason === null ? `${DRIVER_NAMES[kind]}: нет` : unavailableReasonText(reason)
    return <span className="text-ink-muted" data-driver={kind} data-unavailable="true" title={title}>—</span>
  }
  const description = marketDriverDescription(driver)
  return (
    <span
      role="img"
      aria-label={`${DRIVER_NAMES[kind]}: ${description}`}
      title={description}
      data-driver={kind}
      data-driver-active={driver.active ? 'true' : 'false'}
      data-driver-ending-soon={driver.endingSoon ? 'true' : undefined}
      className={`inline-flex size-6 items-center justify-center rounded border ${kind === 'promo' ? 'border-gold/50 text-gold-deep' : 'border-rule-strong text-ink'} ${driver.endingSoon ? 'ring-1 ring-st-unpaid/50' : ''}`}
      style={{ opacity: driver.active ? ACTIVE_OPACITY : INACTIVE_OPACITY }}
    >
      {kind === 'promo' ? <PromoGlyph /> : <BoostGlyph />}
    </span>
  )
}
